"use client"

import { useEffect, useState } from "react"
import type { LucideIcon } from "lucide-react"
import { DollarSign, Droplets, ExternalLink, FlaskConical, Ruler, Thermometer } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Interactive recreation of the extension popup for the landing hero. Auto-cycles
 * through all five tools so visitors see the product's full value (recognition
 * over recall), and the tabs are clickable so they can drive it themselves —
 * clicking pauses the auto-rotation (the user took control). Honors
 * prefers-reduced-motion (no auto-cycle). Mirrors the real popup's visuals:
 * lucide tab icons, the real app icon, a brand-green figure, and the
 * external-link CTA.
 */
type Demo = {
  id: string
  Icon: LucideIcon
  label: string
  figure: string
  unit: string
  note: string
  cta: string
  chips: { label: string; strong?: boolean }[]
}

const DEMOS: Demo[] = [
  {
    id: "fertilizer",
    Icon: FlaskConical,
    label: "Fertilizer",
    figure: "35 lbs",
    unit: "/ 1 bag (~$47)",
    note: "When soil temperature reaches 65°F",
    cta: "Get my full Bermuda plan",
    chips: [{ label: "N 0.7", strong: true }, { label: "P 0.4" }, { label: "K 0.6" }],
  },
  {
    id: "size",
    Icon: Ruler,
    label: "Size",
    figure: "3,500 sq ft",
    unit: "/ 0.08 acres",
    note: "Mower: 30–42 inch riding mower",
    cta: "See my full lawn breakdown",
    chips: [{ label: "Seed 18 lb", strong: true }, { label: "Broadcast spreader" }],
  },
  {
    id: "watering",
    Icon: Droplets,
    label: "Watering",
    figure: '1.2"',
    unit: "/ week",
    note: "Summer: water deeply, less often",
    cta: "See my full watering schedule",
    chips: [{ label: "3,738 gal/wk", strong: true }, { label: "3–4×/week" }],
  },
  {
    id: "cost",
    Icon: DollarSign,
    label: "Cost",
    figure: "$530",
    unit: "/ year",
    note: "West Coast · ~$44/month",
    cta: "See my full cost breakdown",
    chips: [{ label: "DIY", strong: true }, { label: "Standard service" }],
  },
  {
    id: "soil",
    Icon: Thermometer,
    label: "Soil",
    figure: "65°F",
    unit: "soil temp",
    note: "Transition period",
    cta: "See the full soil-temp trend",
    chips: [{ label: "65–70°F", strong: true }, { label: "Warming 1.3°/day" }],
  },
]

const ROTATE_MS = 3200

export function PopupDemo() {
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused) return
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return
    const id = setInterval(() => setActive((a) => (a + 1) % DEMOS.length), ROTATE_MS)
    return () => clearInterval(id)
  }, [paused])

  const d = DEMOS[active]

  return (
    <div className="relative w-[340px] max-w-[86vw]">
      <div className="mx-auto mb-3 flex w-fit items-center gap-2 rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-[12px] font-semibold text-gray-500 shadow-sm">
        <img src="/brand/icon-default.png" alt="" className="h-5 w-5 rounded-md" />
        Pinned in your toolbar
      </div>
      <div className="rounded-[20px] shadow-[0_40px_80px_-24px_rgba(16,40,24,0.4)]">
       <div className="ds-glass overflow-hidden rounded-[20px]">
        <div className="flex items-center gap-2 px-4 py-3">
          <img src="/brand/icon-default.png" alt="" className="h-6 w-6 rounded-md" />
          <span className="text-[14px] font-bold text-gray-900">
            What <span className="text-green-600">Grass</span> Is This?
          </span>
        </div>
        <div className="grid grid-cols-5 gap-1 px-3">
          {DEMOS.map((t, i) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setActive(i)
                setPaused(true)
              }}
              aria-label={`Show ${t.label}`}
              aria-pressed={i === active}
              className={cn(
                "flex flex-col items-center gap-1 rounded-lg py-1.5 text-[10px] font-semibold transition-colors",
                i === active ? "bg-white text-green-700 shadow-sm" : "text-gray-400 hover:text-gray-600",
              )}
            >
              <t.Icon className="h-[15px] w-[15px]" aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </div>
        <div className="px-4 pb-4 pt-3">
          <div key={d.id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm duration-300 animate-in fade-in">
            <div className="text-[26px] font-extrabold tracking-tight text-green-700">
              {d.figure} <span className="text-[13px] font-semibold text-gray-400">{d.unit}</span>
            </div>
            <div className="mt-1 text-[12px] text-gray-600">{d.note}</div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {d.chips.map((c) => (
                <span
                  key={c.label}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[11px] font-semibold",
                    c.strong ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600",
                  )}
                >
                  {c.label}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5 rounded-[10px] bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-3 text-[14px] font-bold text-white shadow-md">
            {d.cta}
            <ExternalLink className="h-[15px] w-[15px]" aria-hidden="true" />
          </div>
        </div>
       </div>
      </div>
    </div>
  )
}
